import React, { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { Alert, Props as AlertProps } from './Alert';

type Props = {
  id: string | number;
  duration?: number | null;
} & Omit<AlertProps, 'variant'>;

type ToastPosition = 'top' | 'top-right' | 'top-left' | 'bottom' | 'bottom-right' | 'bottom-left';

export function useAlert(
  variant: AlertProps['variant'] = 'success',
  context: 'toast' | 'inline' = 'toast',
  position: ToastPosition = 'top-right',
  defaultDuration?: number | null
) {
  /**
   * Hooks
   */
  const toast = useToast({
    // Defaults
    position,
    duration: defaultDuration,
  });

  const [isVisible, setIsVisible] = useState(true);

  return React.useCallback(
    ({ id, title, description, duration, isClosable = true, showIcon = true, actions, onClose = () => {} }: Props) => {
      /**
       * Methods
       */
      const handleOnClose = (e?: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
        setIsVisible(false);
        onClose(e);
      };

      // context is inline
      if (context === 'inline') {
        if (isVisible) {
          return (
            <Alert
              variant={variant}
              title={title}
              description={description}
              isClosable={isClosable}
              showIcon={showIcon}
              actions={actions}
              onClose={handleOnClose}
            />
          );
        }

        return null;
      }

      // otherwise toast
      if (toast.isActive(id)) {
        return null;
      }

      toast({
        id,
        isClosable,
        duration: duration !== undefined ? duration : defaultDuration,
        onCloseComplete: handleOnClose,
        render: () => (
          <Alert
            variant={variant}
            title={title}
            description={description}
            isClosable={isClosable}
            showIcon={showIcon}
            actions={actions}
            role="alert"
            onClose={e => {
              toast.close(id);
              handleOnClose(e);
            }}
          />
        ),
      });
      return null;
    },
    [variant, context, toast, isVisible]
  );
}
